import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { motion, MotionProps, AnimatePresence } from 'motion/react'
import { cn, pollForElement } from '@/lib/utils'
import { useEffect, useRef } from 'react'
import { usePrivy } from '@privy-io/react-auth'

const DEFAULT_OVERLAY_MOTION: MotionProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { type: 'spring', duration: 0.5 }
}

interface DialogOverlayProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> {
  className?: string
  motionProps?: MotionProps
  children?: React.ReactNode
}

export const DialogOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Overlay>, DialogOverlayProps>(
  ({ className, motionProps, children, ...props }, ref) => {
    return (
      <DialogPrimitive.Overlay asChild {...props}>
        <motion.div
          ref={ref as unknown as React.Ref<HTMLDivElement>}
          {...DEFAULT_OVERLAY_MOTION}
          {...motionProps}
          className={cn('bg-surface-inverted-90 fixed inset-0 z-30 backdrop-blur-xl', className)}
        >
          {children}
        </motion.div>
      </DialogPrimitive.Overlay>
    )
  }
)
DialogOverlay.displayName = 'DialogOverlay'

const DEFAULT_CONTENT_MOTION: MotionProps = {
  initial: { opacity: 0, y: 100 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 100 },
  transition: { bounce: 0, visualDuration: 0.3 }
}

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  className?: string
  motionProps?: MotionProps
  children?: React.ReactNode
}

export const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ className, motionProps, children, ...props }, ref) => {
    const { isModalOpen: privyModalOpen } = usePrivy()
    const privyModalRef = useRef<HTMLDivElement | null>(null)
    useEffect(() => {
      if (privyModalOpen) {
        // privyModalOpen is set to true before the actual
        // element is rendered so we need to poll for it
        return pollForElement<HTMLDivElement>(
          '#privy-dialog',
          (element) => {
            privyModalRef.current = element
            element.classList.add('pointer-events-auto')
          },
          10
        )
      } else {
        privyModalRef.current = null
      }
    }, [privyModalOpen])

    return (
      <motion.div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center">
        <DialogPrimitive.Content
          asChild
          {...props}
          onInteractOutside={(e) => {
            if (privyModalRef.current?.contains(e.target as Node)) {
              e.preventDefault()
              return
            }
          }}
        >
          <motion.div
            ref={ref as unknown as React.Ref<HTMLDivElement>}
            {...DEFAULT_CONTENT_MOTION}
            {...motionProps}
            className={cn(
              'rounded-xs bg-surface border-outline-variant pointer-events-auto fixed z-40 max-h-[90%] w-[90%] max-w-2xl transform overflow-hidden border p-0 shadow-[3px_3px_6px_0px_rgba(0,0,0,0.20)]',
              className
            )}
          >
            {children}
          </motion.div>
        </DialogPrimitive.Content>
      </motion.div>
    )
  }
)
DialogContent.displayName = 'DialogContent'

type DialogRootProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>

const DialogRoot = ({ children, open, defaultOpen, ...props }: DialogRootProps) => {
  const isOpen = open ?? defaultOpen ?? false

  return (
    <DialogPrimitive.Root open={open} defaultOpen={defaultOpen} {...props}>
      <AnimatePresence>
        {isOpen ? <DialogPrimitive.Portal forceMount>{children}</DialogPrimitive.Portal> : null}
      </AnimatePresence>
    </DialogPrimitive.Root>
  )
}

const Dialog = Object.assign(DialogRoot, {
  Overlay: DialogOverlay,
  Content: DialogContent,
  Close: DialogPrimitive.Close
})

export { Dialog }
